import { useContext, useRef } from 'preact/hooks';
import { ModalForm, ModalFormContext, showModalForm } from '@components/modal-form';
import { rpx } from 'client/lib/rpx-client';
import {
  AllowGiftsToggle,
  ExpandableLimitsFields,
  FreeTrialPeriod,
  PayWhatYouCanToggle,
  PricePointName,
  Provider,
  RedirectUrlField,
  RequireContactToCancel,
} from './new-price-form';
import { formDataToPriceLimits } from './signup-limits-tab';
import { AccessLimitsFormFields } from './access-limits-tab';
import { ExpandedPrice } from 'server/payments/service';
import { currencySymbol, decimalToCents, isZeroDecimal } from 'shared/payments/fmt';
import { useMinidoc } from '@components/minidoc';
import { ManualDom } from '@components/manual-dom';
import { cardMiddleware, defaultToolbarActions, minidocToolbar, placeholder } from 'minidoc-editor';
import { mediaCard, mediaMiddleware } from '@components/media-card';
import { toolbarDropdown } from '@components/toolbar-dropdown';
import { useIntl } from 'shared/intl/use-intl';
import { Currency, PaymentType } from 'server/types';

const priceStore = rpx.prices;
const ALWAYS_DURATION = -1;

function CheckoutContentField({
  contentRef,
  initialContent,
}: {
  contentRef: { current: string };
  initialContent: string;
}) {
  const intl = useIntl();
  const editor = useMinidoc({
    doc: initialContent,
    middleware: () => [
      placeholder('Checkout page content...'),
      minidocToolbar([...defaultToolbarActions, toolbarDropdown({ intl, compact: true })]),
      cardMiddleware([mediaCard]),
      mediaMiddleware(),
    ],
    onChange: (content) => {
      contentRef.current = content;
    },
  });

  return (
    // A div, not a label: a label forwards clicks in the text to the first toolbar button.
    <div class="flex flex-col gap-1">
      <span class="font-medium">Checkout page content</span>
      <div class="border rounded-md ring-2 ring-transparent focus-within:ring-indigo-400">
        <header class="border-b flex">
          <ManualDom el={editor.toolbar.root} />
        </header>
        <ManualDom class="grow mini-padded *:min-h-32 *:p-3" el={editor.root} />
      </div>
      <span class="text-xs text-gray-500">
        Optional rich text displayed above the payment form on the checkout page.
      </span>
    </div>
  );
}

function UpdatePriceAmount({
  paymentType,
  priceInCents,
  currency,
  interval,
}: {
  paymentType: PaymentType;
  priceInCents: number;
  currency: Currency;
  interval?: string;
}) {
  if (paymentType === 'free') {
    return null;
  }

  const zeroDecimal = isZeroDecimal(currency);
  const symbol = currencySymbol(currency);
  const defaultValue = zeroDecimal ? `${priceInCents}` : (priceInCents / 100).toFixed(2);
  const step = zeroDecimal ? '1' : '0.01';

  const suffix =
    paymentType === 'subscription'
      ? `every ${interval || 'month'}`
      : paymentType === 'paymentplan'
        ? 'per payment'
        : undefined;

  return (
    <label class="flex flex-col gap-1">
      <span class="font-medium">Price</span>
      <div class="flex items-center gap-2">
        <span class="text-gray-500">{symbol}</span>
        <input
          type="number"
          step={step}
          min="0"
          name="priceInCents"
          value={defaultValue}
          class="inline-ruz-input w-28 appearance-none"
        />
        {suffix && <span class="text-gray-500">{suffix}</span>}
      </div>
    </label>
  );
}

export function showUpdatePriceModal({
  price,
  stripeMetadata,
  supportsStripe,
  onSave,
}: {
  price: ExpandedPrice;
  supportsStripe: boolean;
  stripeMetadata?: Record<string, string>;
  onSave(price: ExpandedPrice): Promise<void>;
}) {
  return showModalForm(() => {
    const { resolve } = useContext(ModalFormContext);
    const hide = () => resolve(price);
    const checkoutContentRef = useRef(price.checkoutContent || '');

    return (
      <ModalForm
        onClose={hide}
        title="Edit price point"
        onSubmit={async (formData) => {
          const accessDuration = parseInt(formData.accessDuration, 10) || ALWAYS_DURATION;
          const freeTrialPeriod = parseInt(formData.freeTrialPeriod) || 0;
          const {
            requireContactToCancel,
            redirectUrl: rawRedirectUrl,
            minPriceInCents: rawMinPrice,
            suggestedPrice: _suggestedPrice,
            priceInCents: rawPriceInCents,
            currency: _currency,
            ...restOfData
          } = formData;
          const payWhatYouCan = !!formData.payWhatYouCan;
          const updatedPrice = await priceStore.updatePrice({
            ...restOfData,
            redirectUrl: rawRedirectUrl || null,
            checkoutContent: checkoutContentRef.current || null,
            ...formDataToPriceLimits(formData),
            freeTrialPeriod,
            payWhatYouCan,
            priceInCents: payWhatYouCan
              ? formData.suggestedPrice
                ? decimalToCents(formData.suggestedPrice, price.currency)
                : undefined
              : rawPriceInCents != null
                ? decimalToCents(rawPriceInCents, price.currency)
                : undefined,
            minPriceInCents: payWhatYouCan ? decimalToCents(rawMinPrice, price.currency) : 0,
            accessDuration: accessDuration === ALWAYS_DURATION ? null : accessDuration,
            priceId: price.id,
            productId: price.productId,
            stripeMetadata: stripeMetadata,
            allowSelfCancel: !requireContactToCancel,
          });
          const result = { ...updatedPrice, accessDuration };
          await onSave(result);
          resolve(result);
        }}
        confirmButtonText="Save price point"
      >
        <PricePointName name={price.name} paymentType={price.paymentType} />
        <UpdatePriceAmount
          paymentType={price.paymentType}
          priceInCents={price.priceInCents}
          currency={price.currency}
          interval={price.interval}
        />
        <PayWhatYouCanToggle
          paymentType={price.paymentType}
          defaultChecked={price.payWhatYouCan}
          defaultMinPrice={price.minPriceInCents}
          defaultSuggestedPrice={price.priceInCents}
          showSuggestedPrice
        />
        <Provider
          paymentType={price.paymentType}
          supportsStripe={supportsStripe}
          checkStripe={!!price.allowStripe}
        />
        <FreeTrialPeriod paymentType={price.paymentType} value={price.freeTrialPeriod} />
        <RequireContactToCancel
          paymentType={price.paymentType}
          defaultValue={!price.allowSelfCancel}
        />
        <AllowGiftsToggle paymentType={price.paymentType} defaultValue={price.isGiftable} />
        <AccessLimitsFormFields accessDuration={price.accessDuration} />
        <RedirectUrlField value={price.redirectUrl} />
        <CheckoutContentField
          contentRef={checkoutContentRef}
          initialContent={price.checkoutContent || ''}
        />
        <ExpandableLimitsFields price={price} />
      </ModalForm>
    );
  });
}
